import { useState, type FormEvent } from "react";
import { ArrowLeft, KeyRound } from "lucide-react";
import { passwordSchema } from "@ubisafe/shared";
import { BrandLogo } from "../components/BrandLogo";
import { PasswordRequirements } from "../components/PasswordRequirements";
import { api, ApiClientError } from "../lib/api";

interface ResetPasswordPageProps {
  token: string;
  onBack: () => void;
}

export function ResetPasswordPage({ token, onBack }: ResetPasswordPageProps) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "A senha não atende aos requisitos.");
      return;
    }
    if (password !== confirmation) {
      setError("A confirmação não corresponde à nova senha.");
      return;
    }
    if (!token) {
      setError("O link de redefinição é inválido.");
      return;
    }

    setLoading(true);
    try {
      await api.resetPassword({ token, newPassword: parsed.data });
      setSuccess(true);
    } catch (requestError) {
      setError(requestError instanceof ApiClientError ? requestError.message : "Não foi possível redefinir a senha.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout auth-layout--compact">
      <section className="auth-hero">
        <BrandLogo />
        <div className="auth-hero__content">
          <span className="eyebrow">Nova credencial</span>
          <h1>Defina uma senha forte e exclusiva.</h1>
          <p>O link é de uso único e deixa de funcionar depois da conclusão.</p>
        </div>
      </section>
      <section className="auth-panel">
        <div className="auth-card">
          <div className="auth-card__icon" aria-hidden="true"><KeyRound /></div>
          <span className="eyebrow eyebrow--dark">Redefinição</span>
          <h2>Criar nova senha</h2>

          {success ? (
            <>
              <div className="form-alert form-alert--success" role="status">Senha redefinida com sucesso. Você já pode entrar.</div>
              <button className="primary-button primary-button--full" type="button" onClick={onBack}>Ir para o login</button>
            </>
          ) : (
            <form onSubmit={(event) => void submit(event)} noValidate>
              <label htmlFor="reset-password">Nova senha</label>
              <input id="reset-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" />
              <PasswordRequirements password={password} />

              <label htmlFor="reset-confirmation">Confirmar nova senha</label>
              <input id="reset-confirmation" type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" />

              {error && <div className="form-alert form-alert--error" role="alert">{error}</div>}
              <button className="primary-button primary-button--full" type="submit" disabled={loading}>
                {loading ? "Salvando…" : "Salvar nova senha"}
              </button>
              <button className="link-button" type="button" onClick={onBack}><ArrowLeft aria-hidden="true" /> Voltar</button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
